import { createSlice } from "@reduxjs/toolkit";
import { normalCase } from "../../global-client";

const getJobProgressStatus = (jobLogs) => {
  let status = "Pending";
  jobLogs.forEach((log) => {
    switch (log.status.toUpperCase()) {
      case "STARTED":
      case "PAUSED":
      case "FINISHED":
        status = normalCase(log.status);
        break;
      default:
        break;
    }
  });
  return status;
};

const calculateTotalDuration = (jobLogs) => {
  let totalDuration = 0;
  let startTime = null;
  let lastStartTime = null;

  jobLogs.forEach((log) => {
    if (log.status.toUpperCase() === "STARTED") {
      startTime = new Date(log.date);
      lastStartTime = startTime.toISOString(); // Remember the last start time
    } else if ((log.status.toUpperCase() === "PAUSED" || log.status.toUpperCase() === "FINISHED") && startTime) {
      const endTime = new Date(log.date);
      totalDuration += (endTime - startTime) / 1000 / 60; // duration in minutes
      startTime = null;
    }
  });
  //console.log("Total Duration Calculated: ", totalDuration);
  return { totalDuration, lastStartTime };
};

export const initialState = {
  loading: false,
  error: null,
  jobs: [],
  job: null,
  pagination: {},
  //bookingsFilter: false,
  totals: {},
  ttotal: 0,
  jobLogs: [],
  messages: [],
  searchResults: null,
  jobLogSearchPagination: {},
  progressStatus: "Pending",
  jobsInProgress: [],
  jobsPaused: [],
  totalDuration: 0,
  lastStartTime: null,
  lastViewedJobNum: null,
  jobLogPhotosCache: {}, // Cache photos by job log ID to preserve across refreshes
};
export const jobsSlice = createSlice({
  name: "jobs",
  initialState,
  reducers: {
    setLoading: (state) => {
      state.loading = true;
    },
    resetLoading: (state) => {
      state.loading = false;
    },
    setJobs: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      const jobsArray = Array.isArray(payload) ? payload : [];
      state.jobs = jobsArray;
      // Populate jobsInProgress and jobsPaused based on PROGRESS_STATUS
      state.jobsInProgress = jobsArray.filter((job) => job.PROGRESS_STATUS === "Started");
      state.jobsPaused = jobsArray.filter((job) => job.PROGRESS_STATUS === "Paused");
    },
    setJob: (state, { payload }) => {
      //state.loading = false; //still loading while fetching messages
      state.error = null;
      if (payload && payload[0]) {
        state.job = payload[0];
      } else {
        state.job = payload;
      }
    },
    setMessages: (state, { payload }) => {
      //state.loading = false;
      state.error = null;
      state.messages = payload;
    },
    setLastViewedJobNum: (state, { payload }) => {
      state.lastViewedJobNum = payload;
    },
    setError: (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    },
    setPagination: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      state.pagination = payload;
    },
    setTotals: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      state.totals = payload;
      state.ttotal = payload[0].TTOTAL || 0;
    },
    // setBookingsFilter: (state, { payload }) => {
    //   state.bookingsFilter = payload;
    // },
    setJobLogs: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      //state.jobLogs = payload;
      const sortedLogs = payload.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
      
      // Merge cached photos if the incoming logs don't have photos
      const logsWithMergedPhotos = sortedLogs.map((log) => {
        // If this log doesn't have photos but we have cached photos for it, use the cache
        if ((!log.photos || log.photos.length === 0) && state.jobLogPhotosCache[log._id]) {
          return {
            ...log,
            photos: state.jobLogPhotosCache[log._id],
          };
        }
        return log;
      });
      
      state.jobLogs = logsWithMergedPhotos;
      const durationData = calculateTotalDuration(logsWithMergedPhotos);
      state.totalDuration = durationData.totalDuration;
      state.lastStartTime = durationData.lastStartTime;

      const status = getJobProgressStatus(logsWithMergedPhotos);
      state.progressStatus = status;
    },
    setJobLogSearchResults: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      state.jobLogs = payload.jobLogs || [];
      state.jobLogSearchPagination = payload.pagination || {};
    },
    setSearchResults: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      state.searchResults = payload;
    },
    resetSearchResults: (state) => {
      state.loading = false;
      state.error = null;
      state.searchResults = null;
    },
    addToJobLogs: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      const newJobLogs = [...state.jobLogs, payload];
      const sortedLogs = newJobLogs.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
      state.jobLogs = sortedLogs;
      const durationData = calculateTotalDuration(sortedLogs);
      state.totalDuration = durationData.totalDuration;
      state.lastStartTime = durationData.lastStartTime;

      const status = getJobProgressStatus(sortedLogs);
      state.progressStatus = status;
    },
    updateJobLog: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      const index = state.jobLogs.findIndex((log) => log._id === payload._id);
      if (index !== -1) {
        state.jobLogs[index] = payload;
        const sortedLogs = state.jobLogs.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
        state.jobLogs = sortedLogs;
        const durationData = calculateTotalDuration(sortedLogs);
        state.totalDuration = durationData.totalDuration;
        state.lastStartTime = durationData.lastStartTime;

        const status = getJobProgressStatus(sortedLogs);
        state.progressStatus = status;
      }
    },
    removeJobLog: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      const filteredLogs = state.jobLogs.filter((log) => log._id !== payload);
      const sortedLogs = filteredLogs.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
      state.jobLogs = sortedLogs;
      const durationData = calculateTotalDuration(sortedLogs);
      state.totalDuration = durationData.totalDuration;
      state.lastStartTime = durationData.lastStartTime;

      const status = getJobProgressStatus(sortedLogs);
      state.progressStatus = status;
    },
    updateJobStatus: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      switch (payload) {
        case "Completed":
          state.job = { ...state.job, COMPLETE: 1 };
          break;
        case "Picked Up":
          state.job = { ...state.job, PICKEDUP: 1 };
          break;
        case "Not Repairable":
          state.job = { ...state.job, IRREPARABLE: 1 };
          break;
        case "Review Requested":
          state.job = { ...state.job, REVIEW_REQUESTED: 1 };
          break;
          
        default:
          break;
      }
    },
    updateJobProgressStatus: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      state.job = { ...state.job, PROGRESS_STATUS: payload };

      // Replace job in state.jobs with updated state.job if JOB_ID matches
      const jobsArray = Array.isArray(state.jobs) ? state.jobs : [];
      state.jobs = jobsArray.map((job) => (job.JOB_ID === state.job.JOB_ID ? { ...state.job } : job));

      state.jobsInProgress = state.jobs.filter((job) => job.PROGRESS_STATUS === "Started");
      state.jobsPaused = state.jobs.filter((job) => job.PROGRESS_STATUS === "Paused");
    },
    cacheJobLogPhotos: (state, { payload }) => {
      // Cache photos by job log ID so they persist across refreshes
      // payload should be an array of job logs with photos
      if (Array.isArray(payload)) {
        payload.forEach((log) => {
          if (log._id && log.photos && log.photos.length > 0) {
            state.jobLogPhotosCache[log._id] = log.photos;
          }
        });
      }
    },
    clearJobPhotosCache: (state) => {
      // Clear the cache when moving to a different job
      state.jobLogPhotosCache = {};
    },
  },
});

export const {
  setLoading,
  resetLoading,
  setJobs,
  setError,
  setPagination,
  setJob,
  setMessages,
  setTotals,
  setJobLogs,
  setJobLogSearchResults,
  addToJobLogs,
  updateJobLog,
  removeJobLog,
  updateJobProgressStatus,
  updateJobStatus,
  setSearchResults,
  resetSearchResults,
  setLastViewedJobNum,
  cacheJobLogPhotos,
  clearJobPhotosCache,
} = jobsSlice.actions;

export default jobsSlice.reducer;

export const jobSelector = (state) => state.jobs;
